import type {
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  UniqueIdentifier,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useState, useCallback, useRef } from "react";

export interface ColumnData {
  id: UniqueIdentifier;
  cardIds: UniqueIdentifier[];
}

export interface BoardDragResult {
  activeId: UniqueIdentifier;
  sourceColumnId: UniqueIdentifier;
  targetColumnId: UniqueIdentifier;
  oldIndex: number;
  newIndex: number;
}

interface UseBoardDragHandlersOptions {
  columns: ColumnData[];
  onMove: (result: BoardDragResult) => void;
}

const CROSS_COLUMN_COOLDOWN_MS = 150;

function findColumnByCardId(
  columns: ColumnData[],
  cardId: UniqueIdentifier,
): ColumnData | undefined {
  return columns.find((col) => col.cardIds.includes(cardId));
}

function findColumnById(
  columns: ColumnData[],
  columnId: UniqueIdentifier,
): ColumnData | undefined {
  return columns.find((col) => col.id === columnId);
}

export function useBoardDragHandlers({
  columns,
  onMove,
}: UseBoardDragHandlersOptions) {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const lastCrossColumnMove = useRef<{
    from: UniqueIdentifier;
    to: UniqueIdentifier;
    timestamp: number;
  } | null>(null);

  // Use refs so drag callbacks always read the latest values,
  // avoiding stale closures during rapid drag events.
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
    lastCrossColumnMove.current = null;
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const cols = columnsRef.current;
    const activeColumn = findColumnByCardId(cols, active.id);
    let overColumn = findColumnByCardId(cols, over.id);
    if (!overColumn) {
      overColumn = findColumnById(cols, over.id);
    }

    if (!activeColumn || !overColumn || activeColumn.id === overColumn.id)
      return;

    const now = Date.now();
    const last = lastCrossColumnMove.current;
    if (
      last &&
      now - last.timestamp < CROSS_COLUMN_COOLDOWN_MS &&
      last.to === activeColumn.id &&
      last.from === overColumn.id
    ) {
      return;
    }

    lastCrossColumnMove.current = {
      from: activeColumn.id,
      to: overColumn.id,
      timestamp: now,
    };

    const activeIndex = activeColumn.cardIds.indexOf(active.id);
    const overIndex = overColumn.cardIds.indexOf(over.id);
    const newIndex = overIndex >= 0 ? overIndex : overColumn.cardIds.length;

    onMoveRef.current({
      activeId: active.id,
      sourceColumnId: activeColumn.id,
      targetColumnId: overColumn.id,
      oldIndex: activeIndex,
      newIndex,
    });
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    lastCrossColumnMove.current = null;

    if (!over) return;

    const cols = columnsRef.current;
    const activeColumn = findColumnByCardId(cols, active.id);
    const overColumn =
      findColumnByCardId(cols, over.id) ?? findColumnById(cols, over.id);

    if (!activeColumn || !overColumn) return;

    if (activeColumn.id === overColumn.id) {
      const oldIndex = activeColumn.cardIds.indexOf(active.id);
      const overIndex = activeColumn.cardIds.indexOf(over.id);

      if (oldIndex !== overIndex && overIndex >= 0) {
        const reordered = arrayMove(activeColumn.cardIds, oldIndex, overIndex);
        onMoveRef.current({
          activeId: active.id,
          sourceColumnId: activeColumn.id,
          targetColumnId: activeColumn.id,
          oldIndex,
          newIndex: reordered.indexOf(active.id),
        });
      }
    }
  }, []);

  return {
    activeId,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
  };
}
